
import axios from 'axios';
import API_CONFIG from '../config/api';

class PostValidationError extends Error {
    constructor(message, details = {}) {
        super(message);
        this.name = 'PostValidationError';
        this.type = 'validation_error';
        this.validationErrors = details.validation_errors || [];
        this.validationWarnings = details.validation_warnings || [];
        this.originalErrorResponse = details.originalErrorResponse;
    }
}

const isAxiosErrorWithResponse = (error) => axios.isAxiosError(error) && error.response?.data;

export const createPost = async (postData) => {
    try {
        const response = await axios.post(`${API_CONFIG.API_URL}/posts/`, {
            page_id: postData.page_id,
            title: postData.title,
            description: postData.description || '',
            post_type: postData.post_type || 'article',
            slug: postData.customSlug || postData.slug,
            is_paid: postData.is_paid || false,
            price: postData.price || 0,
            cover_image_url: postData.cover_image_url || '',
            meta_description: postData.meta_description || '',
            meta_keywords: postData.meta_keywords || '',
            is_published: postData.is_published || false,
            ai_score: postData.ai_score || 0
        });
        return response.data;
    } catch (error) {
        throw new Error(isAxiosErrorWithResponse(error) ? error.response.data.detail : 'Failed to create post');
    }
};

export const getPosts = async (options = {}) => {
    try {
        const params = { skip: options.skip || 0, limit: options.limit || 100 };
        if (options.published !== undefined) params.published = options.published;
        if (options.page_id) params.page_id = options.page_id;
        const response = await axios.get(`${API_CONFIG.API_URL}/posts/`, { params });
        return response.data;
    } catch (error) {
        throw new Error(isAxiosErrorWithResponse(error) ? error.response.data.detail : 'Failed to fetch posts');
    }
};

export const getPostContent = async (pageId, options = {}) => {
    try {
        const params = { skip: options.skip || 0, limit: options.limit || 100 };
        if (options.publishedOnly !== undefined) params.published = options.publishedOnly;
        const response = await axios.get(`${API_CONFIG.API_URL}/posts/page/${pageId}`, { params });
        return response.data.sort((a, b) => a.order_index - b.order_index);
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch content');
    }
};

export const getPostById = async (postId) => {
    try {
        const response = await axios.get(`${API_CONFIG.API_URL}/posts/${postId}`);
        return response.data;
    } catch (error) {
        throw new Error(isAxiosErrorWithResponse(error) ? error.response.data.detail : 'Failed to fetch post');
    }
};

export const getPostBySlug = async (pageSlug, postSlug) => {
    try {
        const response = await axios.get(`${API_CONFIG.API_URL}/public/pages/${pageSlug}/posts/${postSlug}`);
        return response.data;
    } catch (error) {
        throw new Error(isAxiosErrorWithResponse(error) ? error.response.data.detail : 'Failed to fetch post');
    }
};

export const updatePost = async (postId, updateData) => {
    try {
        const is_paid = updateData.is_paid === true;
        const price = is_paid ? (updateData.price || 0) : 0;
        const response = await axios.put(`${API_CONFIG.API_URL}/posts/${postId}`, {
            title: updateData.title,
            description: updateData.description,
            slug: updateData.customSlug || updateData.slug,
            is_paid,
            price,
            ai_score: updateData.ai_score || 0,
            cover_image_url: updateData.cover_image_url,
            meta_description: updateData.meta_description,
            meta_keywords: updateData.meta_keywords,
            is_published: updateData.is_published
        });
        return response.data;
    } catch (error) {
        throw new Error(isAxiosErrorWithResponse(error) ? error.response.data.detail : 'Failed to update post');
    }
};

export const deletePost = async (postId) => {
    try {
        await axios.delete(`${API_CONFIG.API_URL}/posts/${postId}`);
        return true;
    } catch (error) {
        throw new Error(isAxiosErrorWithResponse(error) ? error.response.data.detail : 'Failed to delete post');
    }
};

export const validatePost = async (postId) => {
    try {
        const response = await axios.get(`${API_CONFIG.API_URL}/posts/${postId}/validate`);
        return response.data;
    } catch (error) {
        throw new Error(isAxiosErrorWithResponse(error) ? error.response.data.detail : 'Failed to validate post');
    }
};

export const publishPost = async (postId) => {
    try {
        const response = await axios.patch(`${API_CONFIG.API_URL}/posts/${postId}/publish`, { is_published: true });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400 && error.response.data?.detail?.validation_errors) {
            const errorDetail = error.response.data.detail;
            throw new PostValidationError(errorDetail.message || 'Post validation failed', {
                validation_errors: errorDetail.validation_errors,
                validation_warnings: errorDetail.validation_warnings || [],
                originalErrorResponse: error.response
            });
        }
        throw new Error(isAxiosErrorWithResponse(error) ? error.response.data.detail : 'Failed to publish post');
    }
};

export const unpublishPost = async (postId) => {
    try {
        const response = await axios.patch(`${API_CONFIG.API_URL}/posts/${postId}/unpublish`);
        return response.data;
    } catch (error) {
        throw new Error(isAxiosErrorWithResponse(error) ? error.response.data.detail : 'Failed to unpublish post');
    }
};

export const updatePostStatus = async (postId, isPublished) => {
    try {
        const response = await axios.patch(`${API_CONFIG.API_URL}/posts/${postId}/status`, { is_published: isPublished });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400 && error.response.data?.detail?.validation_errors) {
            const errorDetail = error.response.data.detail;
            throw new PostValidationError(errorDetail.message || 'Post status update validation failed', {
                validation_errors: errorDetail.validation_errors,
                validation_warnings: errorDetail.validation_warnings || [],
                originalErrorResponse: error.response
            });
        }
        throw new Error(isAxiosErrorWithResponse(error) ? error.response.data.detail : 'Failed to update post status');
    }
};

export const bulkPublishPosts = async (postIds, isPublished) => {
    try {
        const response = await axios.patch(`${API_CONFIG.API_URL}/posts/bulk-publish`, { post_ids: postIds, is_published: isPublished });
        return response.data;
    } catch (error) {
        throw new Error(isAxiosErrorWithResponse(error) ? error.response.data.detail : 'Failed to perform bulk publish operation');
    }
};

export const getPostPublishSummary = async (postId) => {
    try {
        const response = await axios.get(`${API_CONFIG.API_URL}/posts/${postId}/publish-summary`);
        return response.data;
    } catch (error) {
        throw new Error(isAxiosErrorWithResponse(error) ? error.response.data.detail : 'Failed to fetch publish summary');
    }
};

export const canPublishPost = async (postId) => {
    try {
        const validation = await validatePost(postId);
        return validation.can_publish;
    } catch (error) {
        return false;
    }
};

export const getPostStatusInfo = (post) => ({
    isPublished: post.is_published,
    publishedAt: post.published_at,
    status: post.is_published ? 'published' : 'draft',
    statusText: post.is_published ? 'Published' : 'Draft',
    statusClass: post.is_published ? 'published' : 'draft',
    publicUrl: post.is_published ? `/${post.page?.slug || 'page'}/${post.slug}` : null
});

export const getContentStats = async (pageId) => {
    try {
        const allContent = await getPostContent(pageId);
        return {
            total: allContent.length,
            published: allContent.filter((item) => item.is_published).length,
            drafts: allContent.filter((item) => !item.is_published).length,
            lastUpdated: allContent.length > 0 ? Math.max(...allContent.map((item) => new Date(item.updated_at).getTime())) : null
        };
    } catch (error) {
        throw new Error('Failed to fetch content statistics');
    }
};

export const toggleContentStatus = async (post_id) => {
    try {
        const response = await axios.patch(`${API_CONFIG.API_URL}/post/${post_id}/status`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to toggle content status');
    }
};

export const reorderPosts = async (updates) => {
    try {
        const response = await axios.patch(`${API_CONFIG.API_URL}/posts/reorder`, updates);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to toggle content status');
    }
};

const postService = {
    createPost,
    getPosts,
    getPostById,
    getPostBySlug,
    updatePost,
    deletePost,
    validatePost,
    publishPost,
    unpublishPost,
    updatePostStatus,
    bulkPublishPosts,
    getPostPublishSummary,
    canPublishPost,
    getContentStats,
    getPostContent,
    toggleContentStatus,
    getPostStatusInfo,
    reorderPosts
};

export default postService;